import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconBackArrowLeft, IconChevronRightSmall, IconEdit3, IconSearch } from '@holbonkit/icons'
import {
    BottomSheet,
    BottomSheetBody,
    BottomSheetClose,
    BottomSheetContent,
    BottomSheetHeader,
    BottomSheetTitle,
    BottomSheetTrigger,
    Box,
    Button,
    Input,
    Text,
    Title,
} from '@holbonkit/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'

import { getSbpBanksList, type SbpBank, type SelfEmployedPayoutMethod } from '@/shared/api'
import { useLocalStorage } from '@/shared/hooks/use-local-storage'
import { BankIcon } from '@/shared/ui'
import { IconSbp } from '@/shared/ui/icon/icon-sbp'

import { EMPTY_PAYOUT_METHOD } from '@/entities/smz'

import { DeleteBottomSheet, EditBottomSheet } from '../bottom-sheets'

import { List, type SbpBankWithLetter } from './list'

import styles from './index.module.scss'

const PAGE_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 300

type SbpBanksAll = SbpBankWithLetter

const toLetter = (name: string) => name.trim().charAt(0).toLocaleUpperCase('ru')

const groupBanks = (banks: SbpBank[]) => {
    const popular: SbpBank[] = []
    const all: SbpBanksAll[] = []
    const seen = new Set<number>()

    for (const bank of banks) {
        if (seen.has(bank.id)) {
            continue
        }

        seen.add(bank.id)

        if (bank.popularity_score === null) {
            all.push({ ...bank, firstLetter: toLetter(bank.name) })
        } else {
            popular.push(bank)
        }
    }

    const firstLetters = [...new Set(all.map((bank) => bank.firstLetter).filter(Boolean))].sort(
        (left, right) => left.localeCompare(right, 'ru'),
    )

    return { all, firstLetters, popular }
}

type TSbpProps = {
    editMode?: boolean
    errorText?: string
    onSelect: (bank: SbpBank) => void
    selectedBank: null | SbpBank
}

export const Sbp = ({ selectedBank, onSelect, errorText, editMode }: TSbpProps) => {
    const navigate = useNavigate()
    const listRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)
    const [searchInput, setSearchInput] = useState('')
    const [nameFilter, setNameFilter] = useState<string | undefined>(undefined)
    const [isOpen, setIsOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [isDeleteMode, setIsDeleteMode] = useState(false)

    const { getValue: getAccountLink, setValue: setAccountLink } =
        useLocalStorage<SelfEmployedPayoutMethod>(
            'withdrawal-funds-account-link',
            EMPTY_PAYOUT_METHOD,
        )

    useEffect(() => {
        const timeoutId = globalThis.setTimeout(() => {
            const nextName = searchInput.trim()

            setNameFilter(nextName.length === 0 ? undefined : nextName)
        }, SEARCH_DEBOUNCE_MS)

        return () => globalThis.clearTimeout(timeoutId)
    }, [searchInput])

    const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPending } =
        useInfiniteQuery({
            queryKey: [
                { url: '/api/v1/account/sbp-banks' },
                { name: nameFilter, limit: PAGE_LIMIT },
            ],
            queryFn: ({ pageParam }) =>
                getSbpBanksList({
                    name: nameFilter,
                    limit: PAGE_LIMIT,
                    cursor: pageParam,
                }),
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (lastPage) =>
                lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
            enabled: isOpen,
        })

    const pages = data?.pages
    const { all, firstLetters, popular } = useMemo(
        () => groupBanks(pages?.flatMap((page) => page.data) ?? []),
        [pages],
    )

    useEffect(() => {
        listRef.current?.scrollTo({ top: 0 })
    }, [nameFilter])

    useEffect(() => {
        const root = listRef.current
        const sentinel = sentinelRef.current

        if (!isOpen || !root || !sentinel) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage && !isError) {
                    void fetchNextPage()
                }
            },
            { root, rootMargin: '160px' },
        )

        observer.observe(sentinel)

        return () => observer.disconnect()
    }, [
        all.length,
        fetchNextPage,
        hasNextPage,
        isError,
        isFetchingNextPage,
        isOpen,
        popular.length,
    ])

    const handleSelect = useCallback(
        (bank: SbpBank) => {
            onSelect(bank)
            setIsOpen(false)
        },
        [onSelect],
    )

    const handleEdit = useCallback(() => {
        setIsEditMode(true)
        setIsOpen(false)
    }, [])

    const handleOpenDelete = useCallback(() => {
        setIsDeleteMode(true)
        setIsEditMode(false)
    }, [])

    const handleDelete = useCallback(() => {
        setAccountLink(EMPTY_PAYOUT_METHOD)
        navigate({ to: '/withdrawal-smz', search: { sbpType: 'deleted' } })
    }, [navigate, setAccountLink])

    return (
        <>
            <BottomSheet open={isOpen} onOpenChange={setIsOpen}>
                <Box>
                    <BottomSheetTrigger
                        render={
                            <Button
                                size='lg'
                                type='button'
                                variant='secondary'
                                className={clsx(
                                    styles['sbp__button'],
                                    errorText && styles['sbp__button--error'],
                                )}
                                endIcon={
                                    <IconChevronRightSmall color='var(--text-and-icon-secondary)' />
                                }
                                startIcon={
                                    selectedBank ?
                                        <BankIcon
                                            name={selectedBank.name}
                                            url={selectedBank.icon_url}
                                        />
                                    :   <IconSbp color='var(--text-and-icon-secondary)' size={24} />
                                }
                            />
                        }
                    >
                        {selectedBank ?
                            <Box className={styles['sbp__button--selected']}>
                                <Text
                                    className={styles['sbp__button--text']}
                                    variant='body-s-14-regular'
                                >
                                    Банк
                                </Text>
                                <Text
                                    className={styles['sbp__button--text-name']}
                                    variant='body-m-16-medium'
                                >
                                    {selectedBank.name}
                                </Text>
                            </Box>
                        :   <Text
                                className={styles['sbp__button--text']}
                                variant='body-m-16-medium'
                            >
                                Выберите банк
                            </Text>
                        }
                    </BottomSheetTrigger>
                    {errorText ?
                        <Text
                            as='p'
                            className={styles['sbp__error-text']}
                            variant='body-s-14-regular'
                        >
                            {errorText}
                        </Text>
                    :   null}
                </Box>
                <BottomSheetContent fullscreen gripper showCloseButton={false}>
                    <BottomSheetHeader className={styles['bottom-sheet__header']}>
                        <BottomSheetTitle
                            render={() => <Title variant='header-20-bold'>Выберите банк</Title>}
                        />
                        <BottomSheetClose
                            className={styles['bottom-sheet__header-back']}
                            data-test-id='sbp-bottom-sheet__back'
                        >
                            <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                        </BottomSheetClose>
                        {editMode ?
                            <button
                                className={styles['bottom-sheet__header-edit']}
                                data-test-id='sbp-bottom-sheet__edit'
                                type='button'
                                onClick={handleEdit}
                            >
                                <IconEdit3 color='var(--text-and-icon-primary)' size={24} />
                            </button>
                        :   null}
                    </BottomSheetHeader>
                    <BottomSheetBody className={styles['bottom-sheet__content']}>
                        <Input
                            className={styles['bottom-sheet__content--input']}
                            label='Поиск банка'
                            startIcon={<IconSearch />}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setSearchInput(event.target.value)
                            }
                        />
                        <div ref={listRef} className={styles['bottom-sheet__content--body']}>
                            <List
                                accountId={getAccountLink().bank_id || null}
                                all={all}
                                firstLetters={firstLetters}
                                isError={isError}
                                isFetchingNextPage={isFetchingNextPage}
                                isPending={isPending}
                                popular={popular}
                                selectedBank={selectedBank}
                                onSelect={handleSelect}
                            />
                            <div ref={sentinelRef} aria-hidden />
                        </div>
                    </BottomSheetBody>
                </BottomSheetContent>
            </BottomSheet>
            <EditBottomSheet
                handleOpenDelete={handleOpenDelete}
                isEditMode={isEditMode}
                setIsEditMode={() => setIsEditMode(false)}
            />

            <DeleteBottomSheet
                handleDelete={handleDelete}
                isDeleteMode={isDeleteMode}
                setIsDeleteMode={() => setIsDeleteMode(false)}
            />
        </>
    )
}
